<script setup>
import { ref, computed, onMounted, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { flowSessionsAPI } from 'dashboard/api/flows';
import { useAlert } from 'dashboard/composables';

const props = defineProps({
  conversationId: { type: Number, required: true },
});

const { t } = useI18n();

const session = ref(null);
const isLoading = ref(false);
const isActing = ref(false);

const progressPercent = computed(() => {
  if (!session.value || !session.value.total_steps) return 0;
  return Math.round(
    (session.value.steps_completed / session.value.total_steps) * 100
  );
});

const statusColor = computed(
  () =>
    ({
      active: 'text-green-500',
      paused: 'text-amber-500',
      completed: 'text-blue-500',
      agent_takeover: 'text-red-500',
    })[session.value?.status] || 'text-n-slate-8'
);

const statusLabel = computed(() => {
  if (!session.value?.status) return '';
  if (session.value.status === 'agent_takeover')
    return t('MINISTRY.FLOW_STATUS.AGENT_TAKEOVER');
  return (
    session.value.status.charAt(0).toUpperCase() + session.value.status.slice(1)
  );
});

async function loadSession() {
  isLoading.value = true;
  try {
    const { data } = await flowSessionsAPI.getByConversation(
      props.conversationId
    );
    session.value = data.session || data;
    if (session.value?.id === undefined) session.value = null;
  } catch {
    session.value = null;
  } finally {
    isLoading.value = false;
  }
}

async function act(action) {
  if (!session.value) return;
  isActing.value = true;
  try {
    let data;
    if (action === 'pause')
      ({ data } = await flowSessionsAPI.pause(session.value.id));
    if (action === 'resume')
      ({ data } = await flowSessionsAPI.resume(session.value.id));
    if (action === 'stop')
      ({ data } = await flowSessionsAPI.stop(session.value.id));
    session.value = data;
    useAlert(`Flow ${action}d successfully.`);
  } catch (e) {
    useAlert(e?.response?.data?.error || `Failed to ${action} flow.`);
  } finally {
    isActing.value = false;
  }
}

watch(() => props.conversationId, loadSession);
onMounted(loadSession);
</script>

<template>
  <div v-if="isLoading" class="flex justify-center py-4">
    <span class="i-lucide-loader-2 size-4 animate-spin text-n-slate-8" />
  </div>

  <div
    v-else-if="session"
    class="rounded-xl border border-n-weak overflow-hidden"
  >
    <!-- Flow header -->
    <div
      class="flex items-center gap-2 px-3 py-2.5 bg-woot-500/10 border-b border-n-weak"
    >
      <span class="i-lucide-zap size-4 text-woot-500" />
      <div class="flex-1 min-w-0">
        <p class="text-xs font-semibold text-n-slate-12 truncate">
          {{ session.flow_name }}
        </p>
        <p class="text-[10px]" :class="statusColor">
          {{ statusLabel }}
        </p>
      </div>
    </div>

    <!-- Progress -->
    <div class="px-3 py-2 border-b border-n-weak">
      <div class="flex justify-between text-[10px] text-n-slate-8 mb-1">
        <span>{{
          t('MINISTRY.FLOW_STATUS.STEP_PROGRESS', {
            current: session.current_step_number || '—',
            total: session.total_steps,
          })
        }}</span>
        <span>{{ progressPercent }}%</span>
      </div>
      <div class="h-1.5 rounded-full bg-n-slate-3 overflow-hidden">
        <div
          class="h-full rounded-full bg-woot-500 transition-all"
          :style="{ width: `${progressPercent}%` }"
        />
      </div>
    </div>

    <!-- Current step -->
    <div
      v-if="session.current_step_message"
      class="px-3 py-2 border-b border-n-weak"
    >
      <p class="text-[10px] text-n-slate-8 mb-1 font-medium">
        {{ t('MINISTRY.FLOW_STATUS.CURRENT_STEP') }}
      </p>
      <p class="text-xs text-n-slate-11 line-clamp-2">
        {{ session.current_step_message }}
      </p>
    </div>

    <!-- Response log -->
    <div
      v-if="session.responses?.length"
      class="px-3 py-2 border-b border-n-weak"
    >
      <p class="text-[10px] text-n-slate-8 mb-1.5 font-medium">
        {{ t('MINISTRY.FLOW_STATUS.RESPONSE_LOG') }}
      </p>
      <div class="flex flex-col gap-1.5 max-h-32 overflow-y-auto">
        <div
          v-for="(r, i) in session.responses"
          :key="i"
          class="text-[10px] text-n-slate-9"
        >
          <span class="font-medium text-n-slate-11">{{
            t('MINISTRY.FLOW_STATUS.STEP_LABEL', { number: r.step_number })
          }}</span>
          {{ r.option_selected || r.response_text || '—' }}
        </div>
      </div>
    </div>

    <!-- Actions -->
    <div class="px-3 py-2 flex gap-1.5 flex-wrap">
      <button
        v-if="session.status === 'active'"
        class="text-[10px] px-2 py-1 rounded-md border border-amber-300 text-amber-600 hover:bg-amber-50 transition-colors disabled:opacity-50"
        :disabled="isActing"
        @click="act('pause')"
      >
        <span class="i-lucide-pause size-3 mr-0.5" />{{
          t('MINISTRY.FLOW_STATUS.PAUSE')
        }}
      </button>
      <button
        v-if="session.status === 'paused'"
        class="text-[10px] px-2 py-1 rounded-md border border-green-300 text-green-600 hover:bg-green-50 transition-colors disabled:opacity-50"
        :disabled="isActing"
        @click="act('resume')"
      >
        <span class="i-lucide-play size-3 mr-0.5" />{{
          t('MINISTRY.FLOW_STATUS.RESUME')
        }}
      </button>
      <button
        v-if="['active', 'paused'].includes(session.status)"
        class="text-[10px] px-2 py-1 rounded-md border border-red-300 text-red-500 hover:bg-red-50 transition-colors disabled:opacity-50"
        :disabled="isActing"
        @click="act('stop')"
      >
        <span class="i-lucide-stop-circle size-3 mr-0.5" />{{
          t('MINISTRY.FLOW_STATUS.STOP')
        }}
      </button>
      <button
        class="text-[10px] px-2 py-1 rounded-md border border-n-weak text-n-slate-8 hover:text-n-slate-12 transition-colors"
        @click="loadSession"
      >
        <span class="i-lucide-refresh-cw size-3 mr-0.5" />{{
          t('MINISTRY.FLOW_STATUS.REFRESH')
        }}
      </button>
    </div>
  </div>

  <!-- No active flow -->
  <div v-else class="text-center py-4 text-n-slate-8">
    <span class="i-lucide-zap size-5 mb-1 block mx-auto" />
    <p class="text-xs">{{ t('MINISTRY.FLOW_STATUS.NO_ACTIVE') }}</p>
  </div>
</template>
